import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import {
  ClipboardList, QrCode, Camera, ChevronLeft, Loader2, MapPin,
  CheckCircle2, Building2, Car, Cpu, Wrench, RefreshCw, Home,
  LogOut, AlertTriangle, Gauge, ImagePlus, X, Trash2
} from 'lucide-react';
import {
  getOrdenesT, getOrdenT, completarOrden, checkImei, scraperPatente,
  subirFotoServicio, encolarScrapePatente, getFotoUrl, tecnicoPing
} from '../../lib/gestion4satApi';
import { compressImage } from '../../lib/compressImage';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const tipoLabels = {
  instalacion: 'Instalaci\u00f3n', mantencion: 'Mantenci\u00f3n', asistencia: 'Asistencia',
  revision: 'Revisi\u00f3n', retiro: 'Retiro', otro: 'Otro',
};
const estadoColors = {
  asignada: 'bg-blue-500/10 text-blue-600', en_progreso: 'bg-amber-500/10 text-amber-600',
  completada: 'bg-green-500/10 text-green-600',
};

function TecnicoHeader({ user, onLogout }) {
  return (
    <header className="h-14 border-b border-border bg-card flex items-center px-4 gap-3 sticky top-0 z-30">
      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
        <Wrench className="h-4 w-4 text-white" />
      </div>
      <span className="font-bold text-sm flex-1">4sat T&eacute;cnico</span>
      <span className="text-xs text-muted-foreground hidden sm:block">{user?.nombre}</span>
      <Button variant="ghost" size="icon" onClick={onLogout}><LogOut className="h-4 w-4" /></Button>
    </header>
  );
}

// ============ QR Scanner Component ============
function QRScanner({ onScan, onClose }) {
  const videoRef = useRef(null);
  const scannerRef = useRef(null);

  useEffect(() => {
    let stopped = false;
    const init = async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        const scanner = new Html5Qrcode('qr-reader');
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (text) => { if (!stopped) { onScan(text); scanner.stop().catch(() => {}); } },
          () => {},
        );
      } catch (e) {
        toast.error('No se pudo acceder a la c\u00e1mara');
        onClose();
      }
    };
    init();
    return () => {
      stopped = true;
      if (scannerRef.current) scannerRef.current.stop().catch(() => {});
    };
  }, [onScan, onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div id="qr-reader" className="rounded-xl overflow-hidden" />
        <p className="text-white text-center text-sm mt-3">Apunta al c&oacute;digo QR o barcode del GPS</p>
        <Button variant="outline" className="w-full mt-3" onClick={onClose}>Cancelar</Button>
      </div>
    </div>
  );
}

// ============ Mis Ordenes (list) ============
function MisOrdenes({ userId, onSelect }) {
  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const d = await getOrdenesT({ tecnico_id: userId, limit: '50' });
      setOrdenes(d.ordenes.filter(o => o.estado !== 'cancelada'));
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const pendientes = ordenes.filter(o => o.estado === 'asignada' || o.estado === 'en_progreso');
  const completadas = ordenes.filter(o => o.estado === 'completada');

  return (
    <div className="space-y-4 p-4">
      <h2 className="text-lg font-bold">Mis &Oacute;rdenes ({pendientes.length} pendientes)</h2>
      {loading ? <Skeleton className="h-32 w-full" /> : pendientes.length === 0 ? (
        <Card><CardContent className="text-center py-8 text-muted-foreground">Sin &oacute;rdenes pendientes</CardContent></Card>
      ) : pendientes.map(o => (
        <Card key={o.id} className="cursor-pointer hover:border-emerald-500/40 transition-colors" onClick={() => onSelect(o.id)}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex gap-2">
                <Badge className={estadoColors[o.estado]}>{o.estado}</Badge>
                <Badge variant="outline">{tipoLabels[o.tipo]}</Badge>
              </div>
              {o.prioridad === 'alta' && <Badge className="bg-red-500 text-white">URGENTE</Badge>}
            </div>
            <div className="text-sm font-medium">{o.empresa_razon_social}</div>
            {o.patente_formateada && <div className="text-sm font-mono">{o.patente_formateada}</div>}
            {o.direccion && <div className="text-xs text-muted-foreground mt-1">{o.direccion}</div>}
            {o.fecha_programada && <div className="text-xs text-muted-foreground">Programada: {o.fecha_programada}</div>}
          </CardContent>
        </Card>
      ))}
      {completadas.length > 0 && (
        <>
          <h3 className="text-sm font-semibold text-muted-foreground mt-4">Completadas ({completadas.length})</h3>
          {completadas.slice(0, 5).map(o => (
            <Card key={o.id} className="opacity-60">
              <CardContent className="p-3 flex items-center gap-3">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <div className="text-xs">
                  <span className="font-medium">{tipoLabels[o.tipo]}</span> - {o.empresa_razon_social}
                  {o.patente_formateada && <span className="font-mono ml-1">{o.patente_formateada}</span>}
                </div>
              </CardContent>
            </Card>
          ))}
        </>
      )}
    </div>
  );
}

// ============ Orden Detail + Complete Form ============
function OrdenDetalle({ ordenId, onBack, onComplete }) {
  const [orden, setOrden] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [imeiChecking, setImeiChecking] = useState(false);
  const [imeiInfo, setImeiInfo] = useState(null);
  const [patenteLoading, setPatenteLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);

  const [form, setForm] = useState({
    imei: '', patente: '', vehiculo_marca: '', vehiculo_modelo: '',
    vehiculo_anio: '', vehiculo_tipo: '', notas: '', kilometraje: '',
    coordenadas: null,
  });
  const [fotos, setFotos] = useState([]);
  const [uploadingFoto, setUploadingFoto] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const o = await getOrdenT(ordenId);
        setOrden(o);
        setForm(f => ({
          ...f,
          imei: o.imei_predispuesto || '',
          patente: o.patente || '',
          vehiculo_marca: o.vehiculo_marca || '',
          vehiculo_modelo: o.vehiculo_modelo || '',
        }));
        if (o.imei_predispuesto) handleImeiCheck(o.imei_predispuesto);
      } catch (e) { toast.error(e.message); }
      setLoading(false);
    };
    load();
    // Auto GPS
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => setForm(f => ({...f, coordenadas: { lat: pos.coords.latitude, lng: pos.coords.longitude }})),
        () => {}, { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  }, [ordenId]);

  const handleImeiCheck = async (val) => {
    const clean = val.replace(/\D/g, '');
    if (clean.length < 15) { setImeiInfo(null); return; }
    setImeiChecking(true);
    try {
      const data = await checkImei(clean);
      setImeiInfo(data);
    } catch { /* ignore */ }
    setImeiChecking(false);
  };

  const handleQRScan = (text) => {
    setScanning(false);
    const digits = text.replace(/\D/g, '');
    if (digits.length >= 15) {
      setForm(f => ({...f, imei: digits}));
      handleImeiCheck(digits);
      toast.success(`IMEI escaneado: ${digits}`);
    } else {
      toast.error('C\u00f3digo no contiene IMEI v\u00e1lido');
    }
  };

  const handlePatenteBlur = async () => {
    if (!form.patente || form.patente.length < 4 || form.vehiculo_marca) return;
    setPatenteLoading(true);
    try {
      const r = await scraperPatente(form.patente);
      if (r.datos) {
        setForm(f => ({
          ...f,
          vehiculo_marca: r.datos.marca || f.vehiculo_marca,
          vehiculo_modelo: r.datos.modelo || f.vehiculo_modelo,
          vehiculo_anio: r.datos.anio || f.vehiculo_anio,
          vehiculo_tipo: r.datos.tipo || f.vehiculo_tipo,
        }));
      } else {
        // Scrape returned no data - queue for background retry
        encolarScrapePatente({ patente: form.patente }).catch(() => {});
        toast.info('Datos no disponibles ahora. Se reintentará automáticamente.');
      }
    } catch {
      // Scrape failed - queue for background retry
      encolarScrapePatente({ patente: form.patente }).catch(() => {});
      toast.info('Consulta fallida. Se reintentará cada 5 min automáticamente.');
    }
    setPatenteLoading(false);
  };

  const handleFotoCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFoto(true);
    try {
      const compressed = await compressImage(file, 1920, 0.8);
      // Store locally until service is created
      const preview = URL.createObjectURL(compressed);
      setFotos(prev => [...prev, { file: compressed, preview, pending: true }]);
    } catch { /* ignore */ }
    setUploadingFoto(false);
    e.target.value = '';
  };

  const removeFotoPending = (idx) => {
    setFotos(prev => {
      const next = [...prev];
      if (next[idx]?.preview) URL.revokeObjectURL(next[idx].preview);
      next.splice(idx, 1);
      return next;
    });
  };

  const captureGPS = () => {
    if (!navigator.geolocation) return;
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      pos => { setForm(f => ({...f, coordenadas: { lat: pos.coords.latitude, lng: pos.coords.longitude }})); setGpsLoading(false); },
      () => setGpsLoading(false),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSubmit = async () => {
    if (!form.imei) { toast.error('Escanea o ingresa el IMEI del dispositivo'); return; }
    setSubmitting(true);
    try {
      const result = await completarOrden(ordenId, {
        ...form, fecha_servicio: new Date().toISOString().slice(0, 10),
      });

      // Upload pending photos to the created service
      const pendingFotos = fotos.filter(f => f.pending && f.file);
      for (const foto of pendingFotos) {
        try {
          await subirFotoServicio(result.id, foto.file);
        } catch { /* ignore individual photo errors */ }
      }

      // Queue scraper retry if vehicle data is missing
      if (!form.vehiculo_marca && form.patente) {
        encolarScrapePatente({
          patente: form.patente,
          servicio_id: result.id,
          dispositivo_id: result.dispositivo_id || '',
        }).catch(() => {});
      }

      toast.success(`Servicio registrado${pendingFotos.length ? ` con ${pendingFotos.length} foto(s)` : ''}`);
      onComplete();
    } catch (e) { toast.error(e.message); }
    setSubmitting(false);
  };

  if (loading) return <div className="p-4"><Skeleton className="h-64 w-full" /></div>;
  if (!orden) return <div className="p-4 text-center text-muted-foreground">Orden no encontrada</div>;

  return (
    <div className="p-4 space-y-4 max-w-lg mx-auto pb-24">
      {scanning && <QRScanner onScan={handleQRScan} onClose={() => setScanning(false)} />}

      <Button variant="ghost" size="sm" onClick={onBack} className="mb-2"><ChevronLeft className="h-4 w-4 mr-1" />Volver</Button>

      {/* Orden info */}
      <Card>
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center gap-2">
            <Badge className={estadoColors[orden.estado]}>{orden.estado}</Badge>
            <Badge variant="outline">{tipoLabels[orden.tipo]}</Badge>
          </div>
          <div className="flex items-center gap-2 text-sm"><Building2 className="h-4 w-4 text-emerald-500" />{orden.empresa_razon_social}</div>
          {orden.patente_formateada && <div className="flex items-center gap-2 text-sm font-mono"><Car className="h-4 w-4 text-indigo-500" />{orden.patente_formateada}</div>}
          {orden.direccion && <div className="text-xs text-muted-foreground">{orden.direccion}</div>}
          {orden.notas_admin && <div className="text-xs bg-amber-500/10 p-2 rounded">{orden.notas_admin}</div>}
        </CardContent>
      </Card>

      {/* IMEI Scanner */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Cpu className="h-4 w-4 text-blue-500" />Dispositivo GPS</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input
              value={form.imei}
              onChange={e => { setForm(f => ({...f, imei: e.target.value})); handleImeiCheck(e.target.value); }}
              placeholder="IMEI del dispositivo"
              className="font-mono flex-1"
              data-testid="tecnico-imei"
            />
            <Button onClick={() => setScanning(true)} className="bg-blue-600 hover:bg-blue-700 shrink-0" data-testid="btn-scan-qr">
              <QrCode className="h-5 w-5" />
            </Button>
          </div>
          {imeiChecking && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" />Verificando...</div>}
          {imeiInfo?.found && (
            <div className="p-2 rounded bg-blue-500/10 text-xs space-y-0.5">
              <div className="font-medium text-blue-600">Dispositivo encontrado</div>
              {imeiInfo.patente && <div>Patente: <span className="font-mono">{imeiInfo.patente}</span></div>}
              {imeiInfo.empresa_razon_social && <div>Cliente actual: {imeiInfo.empresa_razon_social}</div>}
            </div>
          )}
          {imeiInfo && !imeiInfo.found && (
            <div className="p-2 rounded bg-amber-500/10 text-xs flex items-center gap-1"><AlertTriangle className="h-3 w-3 text-amber-500" />IMEI nuevo (no registrado)</div>
          )}
        </CardContent>
      </Card>

      {/* Vehicle */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Car className="h-4 w-4 text-indigo-500" />Veh&iacute;culo</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="relative">
            <Label>Patente</Label>
            <Input value={form.patente} onChange={e => setForm(f => ({...f, patente: e.target.value.toUpperCase()}))} onBlur={handlePatenteBlur} className="font-mono uppercase" placeholder="ABCD12" />
            {patenteLoading && <Loader2 className="absolute right-3 top-8 h-4 w-4 animate-spin text-muted-foreground" />}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Marca</Label><Input value={form.vehiculo_marca} onChange={e => setForm(f => ({...f, vehiculo_marca: e.target.value}))} placeholder="TOYOTA" /></div>
            <div><Label>Modelo</Label><Input value={form.vehiculo_modelo} onChange={e => setForm(f => ({...f, vehiculo_modelo: e.target.value}))} placeholder="HILUX" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>A&ntilde;o</Label><Input value={form.vehiculo_anio} onChange={e => setForm(f => ({...f, vehiculo_anio: e.target.value}))} placeholder="2024" /></div>
            <div><Label>Tipo</Label><Input value={form.vehiculo_tipo} onChange={e => setForm(f => ({...f, vehiculo_tipo: e.target.value}))} placeholder="CAMIONETA" /></div>
          </div>
        </CardContent>
      </Card>

      {/* KM + Notes + GPS */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Detalles</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="flex items-center gap-1"><Gauge className="h-3 w-3" />Kilometraje</Label>
            <Input value={form.kilometraje} onChange={e => setForm(f => ({...f, kilometraje: e.target.value}))} placeholder="Ej: 45320" type="number" data-testid="input-km" />
          </div>
          <div>
            <Label>Notas</Label>
            <Textarea value={form.notas} onChange={e => setForm(f => ({...f, notas: e.target.value}))} placeholder="Observaciones del servicio..." rows={2} />
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
            <div className="flex items-center gap-2">
              <MapPin className={`h-4 w-4 ${form.coordenadas ? 'text-green-500' : 'text-muted-foreground'}`} />
              {form.coordenadas ? (
                <span className="text-xs font-mono">{form.coordenadas.lat.toFixed(5)}, {form.coordenadas.lng.toFixed(5)}</span>
              ) : <span className="text-xs text-muted-foreground">Sin GPS</span>}
            </div>
            <Button variant="ghost" size="sm" onClick={captureGPS} disabled={gpsLoading}>
              {gpsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Fotos (opcionales) */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><ImagePlus className="h-4 w-4 text-purple-500" />Fotos del servicio (opcional)</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <label>
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFotoCapture} disabled={uploadingFoto} data-testid="input-foto" />
            <div className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-purple-500/50 transition-colors">
              {uploadingFoto ? (
                <div className="flex items-center justify-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Comprimiendo...</div>
              ) : (
                <><Camera className="h-5 w-5 mx-auto text-muted-foreground mb-1" /><p className="text-xs text-muted-foreground">Tomar foto o seleccionar imagen</p></>
              )}
            </div>
          </label>
          {fotos.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {fotos.map((f, i) => (
                <div key={i} className="relative group">
                  <img src={f.preview} alt={`Foto ${i + 1}`} className="w-full h-20 object-cover rounded-lg" />
                  <button onClick={() => removeFotoPending(i)} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Submit */}
      <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-base" data-testid="btn-completar">
        {submitting ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <CheckCircle2 className="h-5 w-5 mr-2" />}
        Completar Servicio
      </Button>
    </div>
  );
}

// ============ Main Tecnico Page ============
export default function G4sTecnicoPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { ordenId } = useParams();
  const [selectedOrden, setSelectedOrden] = useState(ordenId || null);
  const [tab, setTab] = useState('lista');
  const [mapaOrdenes, setMapaOrdenes] = useState([]);
  const [userLoc, setUserLoc] = useState(null);
  const [MapComponent, setMapComponent] = useState(null);

  useEffect(() => { if (ordenId) setSelectedOrden(ordenId); }, [ordenId]);

  // Lazy load map component
  useEffect(() => {
    if (tab === 'mapa' && !MapComponent) {
      import('../../components/gestion4sat/G4sMapaOrdenes').then(mod => {
        setMapComponent(() => mod.G4sMapaOrdenes);
      });
    }
  }, [tab, MapComponent]);

  // Load map data
  useEffect(() => {
    if (tab === 'mapa' && user?.id) {
      import('../../lib/gestion4satApi').then(api => {
        api.getOrdenesMapa({ tecnico_id: user.id }).then(d => setMapaOrdenes(d.ordenes)).catch(() => {});
      });
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          pos => setUserLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
          () => {}, { enableHighAccuracy: true }
        );
      }
    }
  }, [tab, user?.id]);

  const handleSelectFromMap = (id) => {
    setSelectedOrden(id);
    setTab('lista');
    navigate(`/gestion4sat/tecnico/orden/${id}`, { replace: true });
  };

  // Auto-ping GPS every 2 minutes
  useEffect(() => {
    const sendPing = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          pos => {
            tecnicoPing({ lat: pos.coords.latitude, lng: pos.coords.longitude }).catch(() => {});
          },
          () => {}, { enableHighAccuracy: true, timeout: 5000 }
        );
      }
    };
    sendPing();
    const interval = setInterval(sendPing, 120000);
    return () => clearInterval(interval);
  }, []);

  if (selectedOrden) {
    return (
      <div className="min-h-screen bg-background" data-testid="g4s-tecnico-page">
        <TecnicoHeader user={user} onLogout={logout} />
        <OrdenDetalle
          ordenId={selectedOrden}
          onBack={() => { setSelectedOrden(null); navigate('/gestion4sat/tecnico', { replace: true }); }}
          onComplete={() => { setSelectedOrden(null); navigate('/gestion4sat/tecnico', { replace: true }); }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="g4s-tecnico-page">
      <TecnicoHeader user={user} onLogout={logout} />
      {/* Tabs */}
      <div className="flex gap-1 bg-muted p-1 mx-4 mt-3 rounded-lg w-fit">
        <button onClick={() => setTab('lista')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1 ${tab === 'lista' ? 'bg-background shadow-sm' : 'text-muted-foreground'}`} data-testid="tab-lista">
          <ClipboardList className="h-4 w-4" /> Mis &Oacute;rdenes
        </button>
        <button onClick={() => setTab('mapa')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1 ${tab === 'mapa' ? 'bg-background shadow-sm' : 'text-muted-foreground'}`} data-testid="tab-mapa">
          <MapPin className="h-4 w-4" /> Mapa
        </button>
      </div>

      {tab === 'lista' && (
        <MisOrdenes userId={user?.id} onSelect={(id) => { setSelectedOrden(id); navigate(`/gestion4sat/tecnico/orden/${id}`, { replace: true }); }} />
      )}

      {tab === 'mapa' && (
        <div className="p-4">
          {MapComponent ? (
            <MapComponent ordenes={mapaOrdenes} userLocation={userLoc} onSelectOrden={handleSelectFromMap} />
          ) : (
            <div className="text-center py-12 text-muted-foreground">Cargando mapa...</div>
          )}
        </div>
      )}
    </div>
  );
}
